import { useState, useEffect } from 'react';
import { Box, Grid, TextField, CircularProgress, Button, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../../components/FormDialog';
import PickerField from '../../../../components/PickerField';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';

import { createTireDamage, updateTireDamage, getTireDamageById } from '../../../../stores/tireDamageStore';
import { useCompanyDataList } from '../../../../stores/companyStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import MasterCompanyAddEdit from '../../../master-data/company/MasterCompanyAddEdit';

const getInitials = (name) => {
  if (!name) return '';
  const cleaned = name.trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  const consonants = cleaned.replace(/[aeiouAEIOU\s]/g, '');
  if (consonants.length >= 3) return consonants.substring(0, 3).toUpperCase();
  return cleaned.substring(0, 3).toUpperCase();
};

const TireDamageAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company: typeof window !== 'undefined' ? localStorage.getItem('companyId') || '' : '',
    code_internal: '',
    name: '',
    description: '',
    area: '',
    cause: '',
    recommend_action: ''
  });

  const [loading, setLoading] = useState(false);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getTireDamageById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues((prev) => ({
            ...prev,
            company: data.company_id ?? data.company ?? prev.company,
            code_internal: data.code_internal ?? prev.code_internal,
            name: data.name ?? prev.name,
            description: data.description ?? prev.description,
            area: data.area ?? prev.area,
            cause: data.cause ?? prev.cause,
            recommend_action: data.recommend_action ?? prev.recommend_action
          }));
        }
      } catch (err) {
        console.error('Failed to load tire damage:', err);
        notify({ message: t('general.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    company: Yup.string().required(t('general.validationCompanyRequired', 'Company is required')),
    name: Yup.string().required(t('general.validationNameRequired', 'Name is required')),
    code_internal: Yup.string().required('Code Internal is required'),
    area: Yup.string().required('Area is required'),
    cause: Yup.string().required('Cause is required'),
    recommend_action: Yup.string().required('Recommend Action is required')
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          company: Number(values.company),
          code_internal: values.code_internal,
          name: values.name ?? '',
          description: values.description ?? '',
          area: values.area,
          cause: values.cause,
          recommend_action: values.recommend_action
        };

        if (id) {
          await updateTireDamage(id, payload);
          notify({ message: t('general.updateSuccess', 'Data updated successfully'), severity: 'success' });
        } else {
          await createTireDamage(payload);
          notify({ message: t('general.createSuccess', 'Data added successfully'), severity: 'success' });
        }
        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    const damageName = formik.values.name;
    if (damageName) {
      const initials = getInitials(damageName);
      if (initials) {
        formik.setFieldValue('code_internal', `${initials}001`);
      }
    } else {
      formik.setFieldValue('code_internal', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.name]);

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('tireDamage.title', 'Damage')
          : t('general.add', 'Add') + ' ' + t('tireDamage.title', 'Damage')
      }
      maxWidth="sm"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <PickerField
                  label={t('general.company', 'Company') + ' *'}
                  value={formik.values.company}
                  onChange={(val) => formik.setFieldValue('company', val ?? '')}
                  error={formik.touched.company && !!formik.errors.company}
                  helperText={formik.touched.company && formik.errors.company}
                  options={companyPickerParams.options}
                  loading={companyPickerParams.loading}
                  onSearch={companyPickerParams.handleSearch}
                  onScrollEnd={companyPickerParams.handleScrollEnd}
                  allowAdd
                  AddComponent={MasterCompanyAddEdit}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  dialogTitle={t('general.selectCompany', 'Select Company')}
                />
              </Grid>

              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="name"
                  label={t('general.name', 'Name') + ' *'}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && !!formik.errors.name}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="code_internal"
                  label={t('general.code', 'Code') + ' *'}
                  value={formik.values.code_internal}
                  disabled
                  error={formik.touched.code_internal && !!formik.errors.code_internal}
                  helperText={formik.touched.code_internal && formik.errors.code_internal}
                />
              </Grid>

              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  select
                  name="area"
                  label={t('general.area', 'Area') + ' *'}
                  value={formik.values.area}
                  onChange={formik.handleChange}
                  error={formik.touched.area && !!formik.errors.area}
                  helperText={formik.touched.area && formik.errors.area}
                >
                  <MenuItem value="Tread">Tread</MenuItem>
                  <MenuItem value="Sidewall">Sidewall</MenuItem>
                  <MenuItem value="Bead">Bead</MenuItem>
                  <MenuItem value="Inner">Inner</MenuItem>
                </TextField>
              </Grid>

              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  select
                  name="cause"
                  label={t('tireRepairScrap.cause', 'Cause') + ' *'}
                  value={formik.values.cause}
                  onChange={formik.handleChange}
                  error={formik.touched.cause && !!formik.errors.cause}
                  helperText={formik.touched.cause && formik.errors.cause}
                >
                  <MenuItem value="Operational">Operational</MenuItem>
                  <MenuItem value="Maintenance">Maintenance</MenuItem>
                  <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                </TextField>
              </Grid>

              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  select
                  name="recommend_action"
                  label={t('tireRepairScrap.recommendation', 'Recommend Action') + ' *'}
                  value={formik.values.recommend_action}
                  onChange={formik.handleChange}
                  error={formik.touched.recommend_action && !!formik.errors.recommend_action}
                  helperText={formik.touched.recommend_action && formik.errors.recommend_action}
                >
                  <MenuItem value="Repairable">Repairable</MenuItem>
                  <MenuItem value="Retreadable">Retreadable</MenuItem>
                  <MenuItem value="Scrap">Scrap</MenuItem>
                </TextField>
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="description"
                  label={t('general.description', 'Description')}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && !!formik.errors.description}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default TireDamageAddEdit;
